import React, { useState } from 'react';
import { POSITIONS } from '../../hooks/useGameState';
import FieldingLogModal from './FieldingLogModal';
import InningOutsPicker from './InningOutsPicker';

// ─────────────────────────────────────────────
// POSITION ENTRY MODAL
// ─────────────────────────────────────────────
function PositionEntryModal({ position, onConfirm, onCancel, minInning = 1, minOuts = 0, initialInning, initialOuts }) {
  const [inning, setInning] = useState(initialInning ?? minInning);
  const [outs, setOuts] = useState(initialOuts ?? minOuts);
  return (
    <div className="fld-modal-overlay">
      <div className="fld-modal-card">
        <div className="fld-modal-title">Playing {position}</div>
        <div className="fld-modal-body">When did they enter at {position}?</div>
        <InningOutsPicker
          label="Inning entered"
          minInning={minInning}
          minOuts={minOuts}
          inning={inning}
          outs={outs}
          onInningChange={setInning}
          onOutsChange={setOuts}
        />
        <div className="fld-modal-actions">
          <button className="fld-modal-confirm" onClick={() => onConfirm(inning, outs)}>
            Start Tracking {position}
          </button>
          <button className="fld-modal-cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// POSITION EXIT MODAL
// ─────────────────────────────────────────────
function PositionExitModal({ position, entryInning, onConfirm, onCancel, initialInning, initialOuts, minInning, minOuts }) {
  const [inning, setInning] = useState(initialInning ?? minInning ?? entryInning ?? 1);
  const [outs, setOuts] = useState(initialOuts ?? minOuts ?? 0);
  return (
    <div className="fld-modal-overlay">
      <div className="fld-modal-card">
        <div className="fld-modal-title">Leaving {position}?</div>
        <div className="fld-modal-body">When did they stop playing {position}?</div>
        <InningOutsPicker
          label="Inning removed"
          minInning={minInning ?? entryInning ?? 1}
          minOuts={minOuts ?? 0}
          inning={inning}
          outs={outs}
          onInningChange={setInning}
          onOutsChange={setOuts}
        />
        <div className="fld-modal-actions">
          <button className="fld-modal-confirm" onClick={() => onConfirm(inning, outs)}>
            Confirm
          </button>
          <button className="fld-modal-cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// FIELDING ACTION BUTTONS
// Context-aware: catcher gets extra options
// ─────────────────────────────────────────────

const STAT_TOOLTIPS = {
  putout: 'The player who records the final out. Example: 1st baseman catching a throw, outfielder catching a fly ball, catcher tagging a runner.',
  assist: 'The player who throws or deflects the ball to help get an out. Example: shortstop fielding a grounder and throwing to 1st base.',
  putout_assist: 'Player did both on the same play. Example: shortstop catches a throw at 2nd for one out, then throws to 1st for the second out.',
  error: 'A mistake that allowed a batter or runner to advance. Example: dropped throw, wild throw, or misplayed ground ball.',
  error_fielding: 'The player dropped or misplayed the ball. Example: outfielder drops a fly ball, 1st baseman drops a throw.',
  error_throwing: 'The player made a bad throw. Example: shortstop throws wildly past 1st base.',
};

function FieldingStatModal({ position, onSelect, onClose }) {
  const [tooltip, setTooltip] = useState(null);
  const [showError, setShowError] = useState(false);
  const [pendingStat, setPendingStat] = useState(null);
  const [pendingPlayType, setPendingPlayType] = useState(null);
  const isFirstBase = position === '1B';
  function toggleTip(key, e) {
    e.stopPropagation();
    setTooltip(prev => prev === key ? null : key);
  }
  function handleStatClick(key) {
    if (key === 'error') { setShowError(true); setTooltip(null); return; }
    if (['putout', 'assist', 'putout_assist'].includes(key)) {
      setPendingStat(key);
      setTooltip(null);
      return;
    }
    onSelect(key, 'single');
    onClose();
  }
  function handlePlayType(playType) {
    const isPutout = pendingStat === 'putout' || pendingStat === 'putout_assist';
    if (isFirstBase && isPutout) {
      setPendingPlayType(playType);
      return;
    }
    onSelect(pendingStat, playType);
    onClose();
  }
  function handleScoop(isScoop) {
    onSelect(pendingStat, pendingPlayType, isScoop);
    onClose();
  }
  return (
    <div className="fld-modal-overlay" onClick={onClose}>
      <div className="fld-modal-card" onClick={e => e.stopPropagation()}>
        {pendingPlayType ? (
          <>
            <div className="fld-modal-title">Clean catch or scoop?</div>
            <div className="fld-stat-option-list">
              {[
                { key: false, label: 'Clean Catch' },
                { key: true,  label: 'Scoop (short hop / dirt)' },
              ].map(({ key, label }) => (
                <div key={String(key)} className="fld-stat-option-row">
                  <button
                    className="fld-stat-option-btn"
                    onClick={() => handleScoop(key)}
                  >
                    {label}
                  </button>
                </div>
              ))}
              <button
                className="fld-modal-back"
                onClick={() => setPendingPlayType(null)}
              >
                ← Back
              </button>
            </div>
          </>
        ) : pendingStat ? (
          <>
            <div className="fld-modal-title">What type of play?</div>
            <div className="fld-stat-option-list">
              {[
                { key: 'single', label: 'Single Out' },
                { key: 'dp',     label: 'Double Play' },
                { key: 'tp',     label: 'Triple Play' },
              ].map(({ key, label }) => (
                <div key={key} className="fld-stat-option-row">
                  <button
                    className="fld-stat-option-btn"
                    onClick={() => handlePlayType(key)}
                  >
                    {label}
                  </button>
                </div>
              ))}
              <button
                className="fld-modal-back"
                onClick={() => setPendingStat(null)}
              >
                ← Back
              </button>
            </div>
          </>
        ) : !showError ? (
          <div className="fld-stat-option-list">
            <div className="fld-modal-title">Tap to add fielding stat</div>
            {[
              { key: 'putout',        label: 'Putout' },
              { key: 'assist',        label: 'Assist' },
              { key: 'putout_assist', label: 'Putout + Assist' },
              { key: 'error',         label: 'Error' },
            ].map(({ key, label }) => (
              <div key={key} className="fld-stat-option-row">
                <button
                  className="fld-stat-option-btn"
                  onClick={() => handleStatClick(key)}
                >
                  {label}
                </button>
                <button
                  className="fld-stat-tip-btn"
                  onClick={e => toggleTip(key, e)}
                >?</button>
              </div>
            ))}
            {tooltip && (
              <div className="fld-stat-tooltip">{STAT_TOOLTIPS[tooltip]}</div>
            )}
          </div>
        ) : (
          <div className="fld-stat-option-list">
            <div className="fld-modal-body">What type of error?</div>
            {[
              { key: 'error_fielding', label: 'Fielding Error' },
              { key: 'error_throwing', label: 'Throwing Error' },
            ].map(({ key, label }) => (
              <div key={key} className="fld-stat-option-row">
                <button
                  className="fld-stat-option-btn"
                  onClick={() => { onSelect(key); onClose(); }}
                >
                  {label}
                </button>
                <button
                  className="fld-stat-tip-btn"
                  onClick={e => toggleTip(key, e)}
                >?</button>
              </div>
            ))}
            {tooltip && (
              <div className="fld-stat-tooltip">{STAT_TOOLTIPS[tooltip]}</div>
            )}
            <button className="fld-modal-cancel" onClick={() => { setShowError(false); setTooltip(null); }}>← Back</button>
          </div>
        )}

        <div className="fld-modal-actions">
          <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CatcherModal({ flow, onAction, onClose }) {
  const [step, setStep] = useState('start');
  const [base, setBase] = useState(null);

  function dispatch(payload) {
    onAction(payload);
    onClose();
  }

  if (flow === 'droppedThird') {
    if (step === 'start') {
      return (
        <div className="fld-modal-overlay" onClick={onClose}>
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Dropped 3rd Strike</div>
            <div className="fld-modal-body">What happened?</div>
            <div className="fld-modal-actions">
              <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'droppedThird', result: 'tagged' })}>Tagged runner</button>
              <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'droppedThird', result: 'threwOut' })}>Threw runner out at 1st</button>
              <button className="fld-modal-confirm" onClick={() => setStep('d3k-safe')}>Runner made it to 1st</button>
              <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="fld-modal-overlay" onClick={onClose}>
        <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
          <div className="fld-modal-title">Dropped 3rd — Runner Safe</div>
          <div className="fld-modal-body">Error on who?</div>
          <div className="fld-modal-actions">
            <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'droppedThird', result: 'safe', errorOn: 'pitcher' })}>Pitcher (wild pitch)</button>
            <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'droppedThird', result: 'safe', errorOn: 'catcher' })}>Catcher (fielding error)</button>
            <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  if (flow === 'stealHome') {
    return (
      <div className="fld-modal-overlay" onClick={onClose}>
        <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
          <div className="fld-modal-title">Steal Home</div>
          <div className="fld-modal-body">How did they score?</div>
          <div className="fld-modal-actions">
            <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'stealHome', how: 'steal' })}>Straight Steal</button>
            <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'stealHome', how: 'passedBall' })}>Passed Ball</button>
            <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'stealHome', how: 'wildPitch' })}>Wild Pitch</button>
            <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  if (flow === 'pickoff') {
    if (step === 'start') {
      return (
        <div className="fld-modal-overlay" onClick={onClose}>
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Pickoff Attempt</div>
            <div className="fld-modal-body">Which base?</div>
            <div className="fld-modal-actions">
              <button className="fld-modal-confirm" onClick={() => { setBase('first');  setStep('pickoff-result'); }}>1st Base</button>
              <button className="fld-modal-confirm" onClick={() => { setBase('second'); setStep('pickoff-result'); }}>2nd Base</button>
              <button className="fld-modal-confirm" onClick={() => { setBase('third');  setStep('pickoff-result'); }}>3rd Base</button>
              <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
            </div>
          </div>
        </div>
      );
    }
    const poBaseLabel = base === 'first' ? '1st' : base === 'second' ? '2nd' : '3rd';
    const receiverPoLabel = base === 'first' ? '1B' : base === 'second' ? '2B / SS' : '3rd Base';
    if (step === 'pickoff-result') {
      return (
        <div className="fld-modal-overlay" onClick={onClose}>
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Pickoff — {poBaseLabel}</div>
            <div className="fld-modal-body">Result?</div>
            <div className="fld-modal-actions">
              <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'pickoffAttempt', base, result: 'safe' })}>Safe — Failed</button>
              <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'pickoffAttempt', base, result: 'pickoff' })}>Thrown Out (Pickoff)</button>
              <button className="fld-modal-confirm" onClick={() => setStep('pickoff-error')}>Error</button>
              <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="fld-modal-overlay" onClick={onClose}>
        <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
          <div className="fld-modal-title">Pickoff {poBaseLabel} — Error</div>
          <div className="fld-modal-body">Whose error?</div>
          <div className="fld-modal-actions">
            <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'pickoffAttempt', base, result: 'error', errorOn: 'catcher', errorType: 'throwing' })}>Catcher — Throwing Error</button>
            <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'pickoffAttempt', base, result: 'error', errorOn: 'receiver', errorType: 'fielding' })}>{receiverPoLabel} — Fielding Error</button>
            <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // flow === 'throw'
  if (step === 'start') {
    return (
      <div className="fld-modal-overlay" onClick={onClose}>
        <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
          <div className="fld-modal-title">Throw Attempt</div>
          <div className="fld-modal-body">Which base?</div>
          <div className="fld-modal-actions">
            <button className="fld-modal-confirm" onClick={() => { setBase('second'); setStep('result'); }}>2nd Base</button>
            <button className="fld-modal-confirm" onClick={() => { setBase('third'); setStep('result'); }}>3rd Base</button>
            <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'result') {
    return (
      <div className="fld-modal-overlay" onClick={onClose}>
        <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
          <div className="fld-modal-title">Throw to {base === 'second' ? '2nd' : '3rd'}</div>
          <div className="fld-modal-body">Result?</div>
          <div className="fld-modal-actions">
            <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'throwAttempt', base, result: 'sbAllowed' })}>Safe (SB Allowed)</button>
            <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'throwAttempt', base, result: 'cs' })}>Thrown Out (CS)</button>
            <button className="fld-modal-confirm" onClick={() => setStep('error')}>Error</button>
            <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  const receiverLabel = base === 'second' ? '2B / SS' : '3rd Base';
  return (
    <div className="fld-modal-overlay" onClick={onClose}>
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="fld-modal-title">Throw to {base === 'second' ? '2nd' : '3rd'} — Error</div>
        <div className="fld-modal-body">Whose error?</div>
        <div className="fld-modal-actions">
          <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'throwAttempt', base, result: 'error', errorOn: 'catcher', errorType: 'throwing' })}>Catcher — Throwing Error</button>
          <button className="fld-modal-confirm" onClick={() => dispatch({ type: 'throwAttempt', base, result: 'error', errorOn: 'receiver', errorType: 'fielding' })}>{receiverLabel} — Fielding Error</button>
          <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function FieldingActions({ position, onAction }) {
  const [statModal, setStatModal] = useState(false);
  const isCatcher = position === 'C';
  const [catcherModal, setCatcherModal] = useState(null);
  const [toast, setToast] = useState(null);

  function showToast(label) {
    setToast(label);
    setTimeout(() => setToast(null), 900);
  }

  function catcherTap(type, toastLabel) {
    onAction({ type, position });
    showToast(toastLabel);
  }


  function handleStatSelect(key, playType = 'single', isScoop = false) {
    if (key === 'putout') {
      onAction({ type: 'putout', situation: 'standard', position, isScoop });
    } else if (key === 'assist') {
      onAction({ type: 'assist', situation: 'standard', position });
    } else if (key === 'putout_assist') {
      onAction({ type: 'putout', situation: 'putout_assist', position, isScoop });
      onAction({ type: 'assist', situation: 'putout_assist', position });
    } else if (key === 'error_fielding') {
      onAction({ type: 'error', situation: 'fielding_error', position, errorType: 'fielding' });
    } else if (key === 'error_throwing') {
      onAction({ type: 'error', situation: 'throwing_error', position, errorType: 'throwing' });
    }
    if (playType === 'dp') {
      onAction({ type: 'dp', situation: 'standard', position });
    } else if (playType === 'tp') {
      onAction({ type: 'tp', situation: 'standard', position });
    }
  }

  return (
    <div className="fld-actions">

      {/* Add fielding stat button */}
      <div className="fld-add-stat-wrap">
        <button className="fld-add-stat-btn" onClick={() => setStatModal(true)}>
          + Add Fielding Stat
        </button>
      </div>

      {/* Catcher-only */}
      {isCatcher && (
        <div className="fld-action-group catcher">
          <div className="fld-catcher-label">Catcher Specific Events</div>
          <div className="fld-catcher-grid">
            <button className="fld-action-btn blue"  onClick={() => catcherTap('strikeout', '+K  +PO')}>Strikeout</button>
            <button className="fld-action-btn green" onClick={() => catcherTap('blockedBall', '+BLK')}>Blocked Ball</button>
            <button className="fld-action-btn blue"  onClick={() => setCatcherModal('throw')}>Steal Attempt (Throw)</button>
            <button className="fld-action-btn green" onClick={() => setCatcherModal('pickoff')}>Pickoff Attempt</button>
            <button className="fld-action-btn amber" onClick={() => catcherTap('passedBall', '+PB')}>Passed Ball</button>
            <button className="fld-action-btn muted" onClick={() => setCatcherModal('droppedThird')}>Dropped 3rd Strike</button>
            <button className="fld-action-btn amber fld-catcher-full" onClick={() => setCatcherModal('stealHome')}>Steal Home</button>
          </div>
        </div>
      )}

      {/* Stat modal */}
      {statModal && (
        <FieldingStatModal
          position={position}
          onSelect={handleStatSelect}
          onClose={() => setStatModal(false)}
        />
      )}

      {/* Catcher modal */}
      {catcherModal && (
        <CatcherModal
          flow={catcherModal}
          onAction={(payload) => { onAction({ ...payload, position }); }}
          onClose={() => setCatcherModal(null)}
        />
      )}

      {/* Tap feedback toast */}
      {toast && (
        <div className="fld-toast" key={toast + Date.now()}>
          {toast}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// POSITION STATS CARD
// Shows current totals for the active position
// ─────────────────────────────────────────────

function PositionStatsCard({ position, data }) {
  if (!data) return null;
  const tc = data.po + data.assists + data.errors;
  const fpct =
    tc > 0 ? ((data.po + data.assists) / tc).toFixed(3).replace(/^0/, '') : '—';
  const isCatcher = position === 'C';
  const isFirstBase = position === '1B';

  return (
    <div className="fld-pos-stats">
      <div className="fld-pos-stats-row">
        {[
          { label: 'PO', value: data.po },
          { label: 'A', value: data.assists },
          { label: 'E', value: data.errors },
          { label: 'DP', value: data.dp },
          { label: 'TP', value: data.tp || 0 },
          { label: 'TC', value: tc },
          { label: 'FPCT', value: fpct },
        ].map(({ label, value }) => (
          <div
            key={label}
            className={`fld-stat-cell ${label === 'E' && data.errors > 0 ? 'error' : ''}`}
          >
            <div className="fld-stat-value">{value}</div>
            <div className="fld-stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Error breakdown */}
      {data.errors > 0 &&
        (data.throwingErrors > 0 || data.fieldingErrors > 0) && (
          <div className="fld-error-breakdown">
            {data.throwingErrors > 0 && (
              <span>Throwing: {data.throwingErrors}</span>
            )}
            {data.fieldingErrors > 0 && (
              <span>Fielding: {data.fieldingErrors}</span>
            )}
          </div>
        )}

      {/* Catcher extras */}
      {isCatcher && (
        <div className="fld-catcher-stats-row">
          {[
            { label: 'BLK', value: data.blockedBalls || 0 },
            {
              label: 'BLK%',
              value:
                (data.blockedBalls || 0) + (data.passedBalls || 0) > 0
                  ? Math.round(
                      ((data.blockedBalls || 0) /
                        ((data.blockedBalls || 0) + (data.passedBalls || 0))) *
                        100
                    ) + '%'
                  : '—',
            },
            { label: 'PB', value: data.passedBalls || 0 },
            { label: 'Scrd/PB', value: data.scoredOnPassedBall || 0 },
            { label: 'SBA', value: data.sbAllowed || 0 },
            { label: 'CS', value: data.caughtStealing || 0 },
            {
              label: 'CS%',
              value:
                (data.sbAllowed || 0) + (data.caughtStealing || 0) > 0
                  ? Math.round(
                      ((data.caughtStealing || 0) /
                        ((data.sbAllowed || 0) + (data.caughtStealing || 0))) *
                        100
                    ) + '%'
                  : '—',
            },
            { label: 'PO-CS', value: data.pickoffCS || 0 },
            { label: 'D3K', value: data.droppedThird || 0 },
          ].map(({ label, value }) => (
            <div key={label} className="fld-stat-cell">
              <div className="fld-stat-value">{value}</div>
              <div className="fld-stat-label">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* First base extras */}
      {isFirstBase && (
        <div className="fld-catcher-stats-row">
          {[
            {
              label: 'RF',
              value: data.innings > 0
                ? (9 * (data.po + data.assists) / data.innings).toFixed(2)
                : '—',
            },
            { label: 'Scoops', value: data.scoops || 0 },
          ].map(({ label, value }) => (
            <div key={label} className="fld-stat-cell">
              <div className="fld-stat-value">{value}</div>
              <div className="fld-stat-label">{label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// ALL POSITIONS SUMMARY
// Compact view of every position played
// ─────────────────────────────────────────────

function AllPositionsSummary({ positions, fieldingTotals, onSelect }) {
  const entries = Object.entries(positions);
  if (entries.length === 0) return null;

  return (
    <div className="fld-summary">
      <div className="fld-summary-title">Game Totals</div>

      {/* Per-position rows */}
      {entries.map(([pos, data]) => {
        return (
          <button
            key={pos}
            className="fld-summary-row"
            onClick={() => onSelect(pos)}
          >
            <span className="fld-summary-pos">{pos}</span>
            <span className="fld-summary-inn">
              {data.innings > 0 ? `${data.innings.toFixed(1)} inn` : ''}
            </span>
            <div className="fld-summary-stats">
              <span>{data.po} PO</span>
              <span>{data.assists} A</span>
              {data.errors > 0 && (
                <span className="fld-err">{data.errors} E</span>
              )}
              {data.dp > 0 && <span>{data.dp} DP</span>}
            </div>
          </button>
        );
      })}

      {/* Aggregate totals */}
      <div className="fld-totals-row">
        <span className="fld-totals-label">Total</span>
        <div className="fld-totals-stats">
          {[
            { label: 'PO', value: fieldingTotals.po },
            { label: 'A', value: fieldingTotals.assists },
            { label: 'E', value: fieldingTotals.errors },
            { label: 'TC', value: fieldingTotals.tc },
            {
              label: 'FPCT',
              value:
                fieldingTotals.fpct > 0
                  ? fieldingTotals.fpct.toFixed(3).replace(/^0/, '')
                  : '—',
            },
          ].map(({ label, value }) => (
            <div
              key={label}
              className={`fld-total-cell ${label === 'E' && fieldingTotals.errors > 0 ? 'error' : ''}`}
            >
              <span className="fld-total-value">{value}</span>
              <span className="fld-total-label">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// RECENT ACTIONS / UNDO
// ─────────────────────────────────────────────

function ActionHistory({ actions, onDelete }) {
  if (actions.length === 0) return null;

  const recent = [...actions].reverse().slice(0, 8);

  return (
    <div className="fld-history">
      <div className="fld-history-label">Recent — tap to undo</div>
      <div className="fld-history-list">
        {recent.map((a) => (
          <div key={a.id} className="fld-history-item">
            <span className="fld-history-text">
              {describeFieldingAction(a)}
            </span>
            <button
              className="fld-undo-btn"
              onClick={() => onDelete(a.id)}
              aria-label="Undo"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// FIELDING GEAR MODAL
// Positions played — entry/exit/IP + edit
// ─────────────────────────────────────────────

function FieldingGearModal({ positions, onEditEntry, onEditExit, onClose, onDeletePosition }) {
  const entries = Object.entries(positions);
  return (
    <div className="fld-modal-overlay" onClick={onClose}>
      <div className="fld-modal-card" onClick={e => e.stopPropagation()}>
        <div className="fld-modal-title">Positions Played</div>
        {entries.length === 0 ? (
          <div className="fld-modal-body">No positions tracked yet.</div>
        ) : (
          <div className="fld-gear-pos-list">
            {entries.map(([pos, data]) => {
              const tc = (data.po || 0) + (data.assists || 0) + (data.errors || 0);
              const fpct = tc > 0 ? ((data.po + data.assists) / tc).toFixed(3).replace(/^0/, '') : '—';
              const isCatcher = pos === 'C';
              return (
                <div key={pos} className="fld-gear-pos-row">
                  <span className="fld-gear-pos-name">{pos}</span>
                  <div className="fld-gear-pos-detail">
                    <button className="fld-gear-edit-btn" onClick={() => onEditEntry(pos)}>
                      <span className="fld-gear-edit-label">Entered</span>
                      <span className="fld-gear-edit-value">
                        {data.entryInning != null
                          ? `${data.entryInning}.${data.entryOuts}`
                          : '—'}
                      </span>
                    </button>
                    <button className="fld-gear-edit-btn" onClick={() => onEditExit(pos)}>
                      <span className="fld-gear-edit-label">Removed</span>
                      <span className="fld-gear-edit-value">
                        {data.exitInning != null
                          ? `${data.exitInning}.${data.exitOuts}`
                          : 'Still in game'}
                      </span>
                    </button>
                    {data.exitInning == null && onDeletePosition && (
                      <button
                        className="fld-modal-confirm"
                        style={{ background: '#c0001a', color: '#fff', marginTop: 6, width: '100%', fontSize: 12 }}
                        onClick={() => {
                          onDeletePosition(pos);
                          onClose();
                        }}
                      >
                        Cancel {pos} Tracking
                      </button>
                    )}
                    {data.innings > 0 && (
                      <span className="fld-gear-pos-ip">{data.innings.toFixed(1)} IP</span>
                    )}
                    {tc > 0 && (
                      <div className="fld-gear-stats-row">
                        {[
                          { label: 'PO', value: data.po || 0 },
                          { label: 'A', value: data.assists || 0 },
                          { label: 'E', value: data.errors || 0 },
                          { label: 'TC', value: tc },
                          { label: 'FPCT', value: fpct },
                        ].map(({ label, value }) => (
                          <div key={label} className={`fld-gear-stat-cell ${label === 'E' && data.errors > 0 ? 'error' : ''}`}>
                            <span className="fld-gear-stat-value">{value}</span>
                            <span className="fld-gear-stat-label">{label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {isCatcher && (data.blockedBalls > 0 || data.passedBalls > 0 || data.sbAllowed > 0 || data.caughtStealing > 0 || data.droppedThird > 0) && (
                      <div className="fld-gear-stats-row">
                        {[
                          { label: 'BLK', value: data.blockedBalls || 0 },
                          { label: 'PB', value: data.passedBalls || 0 },
                          { label: 'SBA', value: data.sbAllowed || 0 },
                          { label: 'CS', value: data.caughtStealing || 0 },
                          { label: 'D3K', value: data.droppedThird || 0 },
                        ].map(({ label, value }) => (
                          <div key={label} className="fld-gear-stat-cell">
                            <span className="fld-gear-stat-value">{value}</span>
                            <span className="fld-gear-stat-label">{label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="fld-modal-actions">
          <button className="fld-modal-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN FIELDING TAB
// ─────────────────────────────────────────────

export default function FieldingTab({ game, pitching }) {
  const { state, fieldingTotals } = game;
  const { fielding } = state;
  const activePos = fielding.activePosition;
  const posData = activePos ? fielding.positions[activePos] : null;
  const visiblePositions = (state.playerIsPitcher ? POSITIONS : POSITIONS.filter(p => p !== 'P')).filter(p => p !== 'DH');

  const [showGearModal, setShowGearModal] = useState(false);
  const [gearEditPos, setGearEditPos] = useState(null);
  const [gearEditMode, setGearEditMode] = useState(null); // 'entry' | 'exit'
  const [editingEntry, setEditingEntry] = useState(false);
  const [editingExit, setEditingExit] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [pendingPosition, setPendingPosition] = useState(null);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  function handleSelectPosition(pos) {
    if (pos === activePos) return; // tapping active position does nothing
    // Existing position — always just switch, no exit modal
    if (fielding.positions[pos]) {
      game.setActivePosition(pos);
    } else {
      // Brand new position — if active pos has no exit, prompt for exit first
      if (activePos && !fielding.positions[activePos]?.exitInning) {
        setPendingPosition(pos);
        setShowExitModal(true);
      } else {
        // No active position or already has exit — go straight to entry modal
        setPendingPosition(pos);
        setShowEntryModal(true);
      }
    }
  }

  function handleEntryConfirm(inning, outs) {
    const pos = pendingPosition;
    game.setActivePosition(pos, inning, outs);
    game.setPositionEntry(pos, inning, outs);
    setPendingPosition(null);
    setShowEntryModal(false);
  }

  function handleDoneAtPosition() {
    setShowExitModal(true);
  }

  function handleExitConfirm(inning, outs) {
    game.setPositionExit(activePos, inning, outs);
    // Now switch to pending position
    if (pendingPosition) {
      if (!fielding.positions[pendingPosition]) {
        setShowEntryModal(true);
        setShowExitModal(false);
      } else {
        game.setActivePosition(pendingPosition);
        setPendingPosition(null);
        setShowExitModal(false);
      }
    } else {
      setShowExitModal(false);
    }
  }

  function handleAction(payload) {
    game.addFieldingAction(payload);
  }

  return (
    <div className="fld-tab">
      {/* ── Modals ───────────────────────── */}
      {editingEntry && (
        <PositionEntryModal
          position={activePos}
          minInning={(() => {
            // Find latest exit among OTHER positions
            const exits = Object.entries(fielding.positions)
              .filter(([p, d]) => p !== activePos && d.exitInning != null);
            if (exits.length === 0) return 1;
            return Math.max(...exits.map(([, d]) => d.exitInning));
          })()}
          minOuts={(() => {
            const exits = Object.entries(fielding.positions)
              .filter(([p, d]) => p !== activePos && d.exitInning != null);
            if (exits.length === 0) return 0;
            const maxExit = exits.map(([, d]) => d).reduce((a, b) =>
              (a.exitInning > b.exitInning || (a.exitInning === b.exitInning && a.exitOuts > b.exitOuts)) ? a : b
            );
            return maxExit.exitOuts ?? 0;
          })()}
          initialInning={posData?.entryInning || 1}
          initialOuts={posData?.entryOuts ?? 0}
          onConfirm={(inning, outs) => {
            game.setPositionEntry(activePos, inning, outs);
            // Recalculate IP if exit exists
            if (posData?.exitInning != null) {
              game.setPositionExit(activePos, posData.exitInning, posData.exitOuts);
            }
            setEditingEntry(false);
          }}
          onCancel={() => setEditingEntry(false)}
        />
      )}
      {editingExit && (
        <PositionExitModal
          position={activePos}
          entryInning={posData?.entryInning || 1}
          initialInning={(() => {
            if (activePos === 'P' && pitching?.pitchingStarted && posData?.exitInning == null) {
              return pitching.currentInning;
            }
            return posData?.exitInning || posData?.entryInning || 1;
          })()}
          initialOuts={(() => {
            if (activePos === 'P' && pitching?.pitchingStarted && posData?.exitOuts == null) {
              const currentInningOuts = (pitching.inningBreakdowns || []).find(b => b.inning === pitching.currentInning)?.outs || 0;
              const reliefPartial = pitching.role === 'Relief' && pitching.currentInning === Math.floor(parseFloat(pitching.reliefInning || 0)) ? Math.round((parseFloat(pitching.reliefInning || 0) % 1) * 10) : 0;
              return Math.min(currentInningOuts + reliefPartial, 2);
            }
            return posData?.exitOuts ?? 0;
          })()}
          minInning={activePos === 'P' && pitching?.pitchingStarted ? pitching.currentInning : (posData?.entryInning || 1)}
          minOuts={activePos === 'P' && pitching?.pitchingStarted ? (() => {
            const currentInningOuts = (pitching.inningBreakdowns || []).find(b => b.inning === pitching.currentInning)?.outs || 0;
            const reliefPartial = pitching.role === 'Relief' && pitching.currentInning === Math.floor(parseFloat(pitching.reliefInning || 0)) ? Math.round((parseFloat(pitching.reliefInning || 0) % 1) * 10) : 0;
            return Math.min(currentInningOuts + reliefPartial, 2);
          })() : 0}
          onConfirm={(inning, outs) => {
            game.setPositionExit(activePos, inning, outs);
            setEditingExit(false);
          }}
          onCancel={() => setEditingExit(false)}
        />
      )}
      {showEntryModal && (
        <PositionEntryModal
          position={pendingPosition}
          onConfirm={handleEntryConfirm}
          onCancel={() => { setPendingPosition(null); setShowEntryModal(false); }}
          minInning={(() => {
            // Find the latest exit inning across all positions
            const exits = Object.values(fielding.positions)
              .filter(p => p.exitInning != null);
            if (exits.length === 0) return 1;
            return Math.max(...exits.map(p => p.exitInning));
          })()}
          minOuts={(() => {
            const exits = Object.values(fielding.positions)
              .filter(p => p.exitInning != null);
            if (exits.length === 0) return 0;
            const maxExit = exits.reduce((a, b) =>
              (a.exitInning > b.exitInning || (a.exitInning === b.exitInning && a.exitOuts > b.exitOuts)) ? a : b
            );
            return maxExit.exitOuts ?? 0;
          })()}
        />
      )}
      {showExitModal && (
        <PositionExitModal
          position={activePos}
          entryInning={posData?.entryInning || 1}
          initialInning={(() => {
            if (activePos === 'P' && pitching?.pitchingStarted) {
              return pitching.currentInning;
            }
            return posData?.entryInning || 1;
          })()}
          initialOuts={(() => {
            if (activePos === 'P' && pitching?.pitchingStarted) {
              const currentInningOuts = (pitching.inningBreakdowns || []).find(b => b.inning === pitching.currentInning)?.outs || 0;
              const reliefPartial = pitching.role === 'Relief' && pitching.currentInning === Math.floor(parseFloat(pitching.reliefInning || 0)) ? Math.round((parseFloat(pitching.reliefInning || 0) % 1) * 10) : 0;
              return Math.min(currentInningOuts + reliefPartial, 2);
            }
            return 0;
          })()}
          minInning={activePos === 'P' && pitching?.pitchingStarted ? pitching.currentInning : (posData?.entryInning || 1)}
          minOuts={activePos === 'P' && pitching?.pitchingStarted ? (() => {
            const currentInningOuts = (pitching.inningBreakdowns || []).find(b => b.inning === pitching.currentInning)?.outs || 0;
            const reliefPartial = pitching.role === 'Relief' && pitching.currentInning === Math.floor(parseFloat(pitching.reliefInning || 0)) ? Math.round((parseFloat(pitching.reliefInning || 0) % 1) * 10) : 0;
            return Math.min(currentInningOuts + reliefPartial, 2);
          })() : 0}
          onConfirm={handleExitConfirm}
          onCancel={() => { setPendingPosition(null); setShowExitModal(false); }}
        />
      )}

      {/* ── Gear modal — positions played ── */}
      {showGearModal && (
        <FieldingGearModal
          positions={fielding.positions}
          onEditEntry={(pos) => { setShowGearModal(false); setGearEditPos(pos); setGearEditMode('entry'); }}
          onEditExit={(pos) => { setShowGearModal(false); setGearEditPos(pos); setGearEditMode('exit'); }}
          onClose={() => setShowGearModal(false)}
          onDeletePosition={(pos) => game.deleteFieldingPosition(pos)}
        />
      )}

      {/* ── Gear modal edit — entry ── */}
      {gearEditPos && gearEditMode === 'entry' && (
        <PositionEntryModal
          position={gearEditPos}
          initialInning={fielding.positions[gearEditPos]?.entryInning || 1}
          initialOuts={fielding.positions[gearEditPos]?.entryOuts ?? 0}
          onConfirm={(inning, outs) => {
            game.setPositionEntry(gearEditPos, inning, outs);
            if (fielding.positions[gearEditPos]?.exitInning != null) {
              game.setPositionExit(gearEditPos, fielding.positions[gearEditPos].exitInning, fielding.positions[gearEditPos].exitOuts);
            }
            setGearEditPos(null);
            setGearEditMode(null);
          }}
          onCancel={() => { setGearEditPos(null); setGearEditMode(null); }}
        />
      )}

      {/* ── Gear modal edit — exit ── */}
      {gearEditPos && gearEditMode === 'exit' && (
        <PositionExitModal
          position={gearEditPos}
          entryInning={fielding.positions[gearEditPos]?.entryInning || 1}
          initialInning={fielding.positions[gearEditPos]?.exitInning || fielding.positions[gearEditPos]?.entryInning || 1}
          initialOuts={fielding.positions[gearEditPos]?.exitOuts ?? 0}
          onConfirm={(inning, outs) => {
            game.setPositionExit(gearEditPos, inning, outs);
            setGearEditPos(null);
            setGearEditMode(null);
          }}
          onCancel={() => { setGearEditPos(null); setGearEditMode(null); }}
        />
      )}

      {/* ── Position selector — always visible ── */}
      <div className="fld-pos-header fld-entry-row">
        <div className="fld-pos-select-wrap">
          <select
            className="fld-pos-select"
            value={activePos || ''}
            onChange={e => { if (e.target.value) handleSelectPosition(e.target.value); }}
          >
            <option value="">Select position…</option>
            {visiblePositions.map(pos => (
              <option key={pos} value={pos}>
                {pos}{fielding.positions[pos]?.innings > 0 ? ` (${fielding.positions[pos].innings.toFixed(1)})` : ''}
              </option>
            ))}
          </select>
        </div>
        {activePos && (
          <>
            <div className="fld-entry-chip" onClick={() => setEditingEntry(true)}>
              <span className="fld-entry-chip-label">Entered</span>
              <span className="fld-entry-chip-value">
                {posData?.entryInning != null
                  ? `${posData.entryInning}.${posData.entryOuts}`
                  : '—'} ✎
              </span>
            </div>
            <div className="fld-entry-chip" onClick={() => setEditingExit(true)}>
              <span className="fld-entry-chip-label">Removed</span>
              <span className="fld-entry-chip-value">
                {posData?.exitInning != null
                  ? `${posData.exitInning}.${posData.exitOuts}`
                  : 'Still in'} ✎
              </span>
            </div>
          </>
        )}
        <button className="fld-fld-gear-btn" onClick={() => setShowGearModal(true)}>⚙️</button>
      </div>

      {/* ── Active position detail ─────────── */}
      {activePos ? (
        <>
          {/* Action buttons */}
          <FieldingActions position={activePos} onAction={handleAction} />

          {/* Compact stat strip — non-catcher only */}
          {activePos !== 'C' && posData && (posData.po > 0 || posData.assists > 0 || posData.errors > 0 || posData.dp > 0) && (
            <div className="fld-stat-strip">
              {[
                { label: 'PO', value: posData.po || 0 },
                { label: 'A', value: posData.assists || 0 },
                { label: 'E', value: posData.errors || 0 },
                { label: 'DP', value: posData.dp || 0 },
                { label: 'TC', value: (posData.po || 0) + (posData.assists || 0) + (posData.errors || 0) },
                {
                  label: 'FPCT',
                  value: (() => {
                    const tc = (posData.po || 0) + (posData.assists || 0) + (posData.errors || 0);
                    return tc > 0 ? ((posData.po + posData.assists) / tc).toFixed(3).replace(/^0/, '') : '—';
                  })()
                },
              ].map(({ label, value }) => (
                <div key={label} className={`fld-strip-cell ${label === 'E' && posData.errors > 0 ? 'error' : ''}`}>
                  <span className="fld-strip-value">{value}</span>
                  <span className="fld-strip-label">{label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Done at position */}
          <div className="fld-remove-wrap">
            {!posData?.exitInning && (
              <button
                className="fld-done-btn"
                onClick={handleDoneAtPosition}
              >
                Done at {activePos}
              </button>
            )}
          </div>
        </>
      ) : (
        <div className="fld-empty-state">
          <div className="fld-empty-icon">🧤</div>
          <div className="fld-empty-text">
            Tap a position above to start tracking
          </div>
        </div>
      )}

      {/* ── Undo + Fielding Log ───────────── */}
      {fielding.actions.length > 0 && (
        <div className="fld-log-btns">
          <button
            className="fld-undo-last-btn"
            onClick={() => {
              const last = fielding.actions[fielding.actions.length - 1];
              if (last) game.deleteFieldingAction(last.id);
            }}
          >
            ↩ Undo Last Action
          </button>
          <button
            className="fld-view-log-btn"
            onClick={() => setShowLog(true)}
          >
            📋 View Fielding Log
          </button>
        </div>
      )}

      <FieldingLogModal
        isOpen={showLog}
        onClose={() => setShowLog(false)}
        actions={fielding.actions}
        positions={fielding.positions}
        onEdit={game.updateFieldingAction}
        onDelete={game.deleteFieldingAction}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
// HELPER: readable action label
// ─────────────────────────────────────────────

function describeFieldingAction(action) {
  const pos = action.position || '';
  switch (action.type) {
    case 'putout':
      return `${pos} — Putout`;
    case 'assist':
      return `${pos} — Assist`;
    case 'dp':
      return `${pos} — Double Play`;
    case 'error':
      return `${pos} — Error (${action.errorType || 'unspecified'})`;
    case 'passedBall':
      return `${pos} — Passed Ball`;
    case 'sbAllowed':
      return `${pos} — SB Allowed`;
    case 'caughtStealing':
      return `${pos} — Caught Stealing`;
    case 'tp':
      return `${pos} — Triple Play`;
    case 'blockedBall':
      return `${pos} — Blocked Ball`;
    case 'strikeout':
      return `${pos} — Strikeout (K)`;
    case 'droppedThird':
      return `${pos} — Dropped 3rd Strike`;
    case 'pickoffCS':
      return `${pos} — Pickoff CS`;
    case 'throwAttempt': {
      const baseLabel = action.base === 'second' ? '2nd' : '3rd';
      if (action.result === 'cs') return `${pos} — CS (throw to ${baseLabel})`;
      if (action.result === 'sbAllowed') return `${pos} — SB Allowed (${baseLabel})`;
      if (action.result === 'error') {
        const who = action.errorOn === 'catcher' ? 'C' : baseLabel;
        return `${pos} — Error on throw to ${baseLabel} (${who})`;
      }
      return `${pos} — Throw to ${baseLabel}`;
    }
    case 'pickoffAttempt':
      if (action.result === 'pickoff') return `${pos} — Pickoff (1st)`;
      if (action.result === 'error') return `${pos} — Error on pickoff attempt`;
      return `${pos} — Pickoff attempt (safe)`;
    case 'stealHome':
      if (action.how === 'steal') return `${pos} — Steal Home allowed`;
      if (action.how === 'passedBall') return `${pos} — Scored on Passed Ball`;
      return `${pos} — Scored on Wild Pitch`;
    default:
      return `${pos} — ${action.type}`;
  }
}
